import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { animate, motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Check } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import VectorField from "@/components/VectorField";
import Marquee from "@/components/Marquee";
import TrustStrip from "@/components/TrustStrip";
import ROICalculator from "@/components/ROICalculator";
import { MaskLine, Reveal, Eyebrow } from "@/components/Reveal";

const METRICS = [
  { value: 30, suffix: "%+", label: "Client sales pipeline growth", detail: "Built digital acquisition funnels, paid campaigns, SEO and content plans for European SMEs and tech distributors." },
  { value: 22, suffix: "%", label: "Faster lead-to-opportunity", detail: "AI-assisted marketing automation and Salesforce CRM pipelines that lower acquisition cost and speed up conversion." },
  { value: null, label: "FIAT — market assessment", detail: "Commercial market assessment covering supply chain workflows, online buying behavior and multichannel retail." },
];

const SERVICES = [
  {
    id: "digital-acquisition",
    n: "01",
    title: "Digital Acquisition Funnels & SEO",
    tagline: "High-velocity pipeline growth for European SMEs & tech distributors",
    img: "https://images.unsplash.com/photo-1470075801209-17f9ec0cada6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwY29ycG9yYXRlJTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmUlMjBtb2Rlcm4lMjBnbGFzcyUyMGR1c2t8ZW58MHx8fHwxNzkxNTU1NzM1fDA&ixlib=rb-4.1.0&q=85",
    points: ["Multi-channel paid acquisition", "Cross-border SEO architecture", "CRO & landing page systems"],
  },
  {
    id: "ai-crm-automation",
    n: "02",
    title: "AI Automation & Salesforce CRM",
    tagline: "Intelligent lead scoring & 22% faster lead-to-opportunity conversion",
    points: ["Salesforce pipeline orchestration", "AI lead enrichment & intent scoring", "Automated nurture sequences"],
  },
  {
    id: "commercial-market-assessment",
    n: "03",
    title: "Commercial Market Assessment",
    tagline: "Enterprise strategy, supply chain optimization & buying behavior analysis",
    points: ["Retail workflow diagnostics", "Buying behavior friction mapping", "Go-to-market playbooks"],
  },
];

function Counter({ value, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, value, { duration: 1.8, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setN(Math.round(v)) });
    return () => c.stop();
  }, [inView, value]);
  return <span ref={ref}>{n}{suffix}</span>;
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      <VectorField className="absolute inset-0 h-full w-full opacity-70" />
      <div className="spot pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-azure-deep/25 blur-[140px]" />

      <motion.div style={{ y, opacity }} className="relative mx-auto w-full max-w-[1440px] px-6 pt-28 pb-20 md:px-10">
        <Eyebrow>Digital acquisition · AI automation · Market intelligence</Eyebrow>

        <h1 className="mt-8 font-display text-[13vw] leading-[0.95] font-extrabold tracking-tight sm:text-7xl lg:text-[6.5rem] 2xl:text-[7.5rem]" data-testid="hero-headline">
          <MaskLine delay={0.15}>GROWTH,</MaskLine>
          <MaskLine delay={0.35} className="text-gradient">ENGINEERED.</MaskLine>
        </h1>

        <Reveal delay={0.6} className="mt-10 max-w-2xl">
          <p className="text-base leading-relaxed text-slate-600 sm:text-lg">
            Vector Growth and Advisory builds digital acquisition funnels, AI-assisted Salesforce
            CRM pipelines and commercial market intelligence for European SMEs, tech distributors
            and enterprise brands — growing client sales pipelines by{" "}
            <span className="font-semibold text-neon">more than 30%</span>.
          </p>
        </Reveal>

        <Reveal delay={0.75} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/contact"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-mono text-xs uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:shadow-[0_0_45px_rgba(47,168,224,0.45)]"
          >
            Get a Growth Audit
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/case-studies"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.22em] text-slate-700 transition-all duration-300 hover:border-neon/60 hover:text-neon"
          >
            Explore Case Studies
          </Link>
        </Reveal>

        <Reveal delay={0.9} className="mt-14 flex flex-wrap gap-3" testid="hero-metric-chips">
          {[["+30%", "client pipeline growth"], ["22%", "faster lead-to-opportunity"], ["FIAT", "commercial market assessment"]].map(([k, v]) => (
            <div key={k} className="flex items-baseline gap-2 rounded-full border hairline bg-surface/60 px-4 py-2 backdrop-blur">
              <span className="font-display text-sm font-extrabold text-neon">{k}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">{v}</span>
            </div>
          ))}
        </Reveal>

        <Reveal delay={0.55} y={0} className="pointer-events-none absolute right-6 top-1/2 hidden xl:block">
          <div className="-translate-y-1/2">
            <div className="clip-frame relative w-[400px] 2xl:w-[440px] border border-azure/30 shadow-2xl" data-testid="hero-image-frame">
              <img
                src="https://images.unsplash.com/photo-1783115259399-3a5a3e0e4592?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxleGVjdXRpdmUlMjBkaWdpdGFsJTIwYWR2aXNvcnklMjBjb25zdWx0YW5jeSUyMGRhdGElMjBhbmFseXRpY3MlMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzkxNTU1NzQyfDA&ixlib=rb-4.1.0&q=85"
                alt="Vector advisory analytics workspace"
                className="h-[420px] w-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 rounded-full glass px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink">
                +30% client pipeline growth
              </div>
            </div>
          </div>
        </Reveal>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500"
      >
        <ArrowDown className="h-5 w-5" />
      </motion.div>
    </section>
  );
}

function Metrics() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32" data-testid="metrics-band">
      <div className="grid gap-px overflow-hidden rounded-2xl border hairline bg-azure/10 md:grid-cols-3">
        {METRICS.map((m, i) => (
          <Reveal key={m.label} delay={i * 0.12} className="h-full">
            <div className="flex h-full flex-col justify-between bg-surface/80 p-8 md:p-10">
              <p className="font-display text-5xl font-extrabold tracking-tight text-gradient md:text-6xl" data-testid={`metric-value-${i}`}>
                {m.value !== null ? <Counter value={m.value} suffix={m.suffix} /> : "FIAT"}
              </p>
              <div className="mt-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-neon/90">{m.label}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">{m.detail}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ServicesBento() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 pb-24 md:px-10 md:pb-32" data-testid="services-bento">
      <Reveal>
        <Eyebrow>Capabilities</Eyebrow>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
          Three systems. <span className="text-gradient">One vector.</span>
        </h2>
      </Reveal>

      <div className="mt-12 grid gap-5 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.1} className="h-full">
            <Link
              to={`/services#${s.id}`}
              data-testid={`service-card-${s.id}`}
              className="group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border hairline bg-surface/70 p-8 transition-all duration-500 hover:border-neon/40 hover:shadow-[0_0_50px_rgba(47,168,224,0.12)]"
            >
              {s.img && (
                <img
                  src={s.img}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover opacity-15 transition-all duration-700 group-hover:scale-105 group-hover:opacity-25"
                />
              )}
              <div className="relative">
                <p className="font-mono text-xs tracking-[0.3em] text-neon/70">{s.n}</p>
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">{s.tagline}</p>
              </div>
              <ul className="relative mt-8 space-y-2.5">
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 text-[13px] text-slate-600">
                    <Check className="h-3.5 w-3.5 shrink-0 text-neon" /> {p}
                  </li>
                ))}
              </ul>
              <span className="relative mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon">
                Explore
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FiatSpotlight() {
  return (
    <section className="relative overflow-hidden border-y hairline bg-surface/30" data-testid="fiat-spotlight">
      <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-6 py-24 md:px-10 md:py-32 lg:grid-cols-2">
        <Reveal>
          <div className="clip-frame relative overflow-hidden border hairline" data-testid="fiat-image-frame">
            <img
              src="https://images.unsplash.com/photo-1765475886628-a7c1fa36b0af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxmaWF0JTIwYXV0b21vYmlsZSUyMGNhciUyMHNob3dyb29tJTIwbW9kZXJuJTIwRXVyb2BlYW4lMjB2ZWhpY2xlfGVufDB8fHx8MTc5MTU1NTczNXww&ixlib=rb-4.1.0&q=85"
              alt="FIAT automobile showroom"
              className="h-[420px] w-full object-cover transition-transform duration-700 hover:scale-105"
              data-testid="fiat-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.28em] text-neon">
              Case file — commercial market assessment
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <Eyebrow>FIAT Automobile</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            Enterprise strategy, mapped at <span className="text-gradient">multichannel scale.</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-slate-600">
            A commercial market assessment for FIAT Automobile — tracing how supply chain
            workflows, online buying behavior and multichannel retail collide on the path to
            purchase, and where the machine loses momentum.
          </p>
          <ul className="mt-8 space-y-3">
            {["Supply chain workflow diagnostics", "Online buying behavior & friction mapping", "Multichannel retail enablement"].map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-slate-600">
                <span className="h-px w-6 bg-neon/60" /> {p}
              </li>
            ))}
          </ul>
          <Link
            to="/case-studies"
            data-testid="fiat-cta-link"
            className="group mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-neon"
          >
            Read the case study
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function InsightsPreview() {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => setPosts([]));
  }, []);

  return (
    <section className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32" data-testid="insights-preview">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>Perspectives & advisory insights</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            Thinking that <span className="text-gradient">moves pipelines.</span>
          </h2>
        </div>
        <Link to="/insights" data-testid="insights-view-all" className="font-mono text-xs uppercase tracking-[0.22em] text-neon link-underline">
          All insights
        </Link>
      </Reveal>

      <div className="mt-12 grid gap-5 md:grid-cols-3" data-testid="insights-grid">
        {posts === null &&
          [0, 1, 2].map((i) => (
            <div key={i} className="h-[360px] animate-pulse rounded-2xl border hairline bg-surface/60" data-testid="insight-skeleton" />
          ))}
        {posts &&
          posts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.1} className="h-full">
              <Link
                to={`/insights/${p.slug}`}
                data-testid={`insight-card-${p.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border hairline bg-surface/70 transition-all duration-500 hover:border-neon/40"
              >
                <div className="h-44 overflow-hidden">
                  <img src={p.cover} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-neon/80">{p.category}</p>
                  <h3 className="mt-3 flex-1 font-display text-lg font-bold leading-snug text-ink">{p.title}</h3>
                  <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    {p.date} · {p.read_time}
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="Vector Growth and Advisory — Growth, Engineered"
        description="Digital acquisition funnels, AI-assisted Salesforce CRM automation and commercial market intelligence for European enterprises. +30% client pipeline growth."
      />
      <Hero />
      <Marquee />
      <TrustStrip />
      <Metrics />
      <ServicesBento />
      <FiatSpotlight />
      <ROICalculator />
      <InsightsPreview />
      <section className="relative overflow-hidden border-t hairline" data-testid="home-cta-band">
        <div className="spot pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-[1440px] px-6 py-28 text-center md:px-10 md:py-36">
          <Reveal>
            <h2 className="mx-auto max-w-4xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
              Let's point your growth in the <span className="text-gradient">right direction.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-slate-500 sm:text-base">
              One conversation. We map where your pipeline leaks and what a Vector system would
              change — funnels, automation, market intelligence.
            </p>
            <Link
              to="/contact"
              data-testid="home-cta-band-button"
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] text-ink transition-all duration-300 hover:shadow-[0_0_45px_rgba(47,168,224,0.45)]"
            >
              Schedule Advisory Call
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
